import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef, type ReactNode } from "react";
import { cn } from "../lib/cn";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

const Overlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-40 bg-scrim", className)}
    {...props}
  />
));
Overlay.displayName = "DialogOverlay";

export interface DialogContentProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  title: string;
  /** Monospace subtitle under the title, e.g. the query or path. */
  subtitle?: string;
  /** Right-aligned header actions, before the close button. */
  actions?: ReactNode;
  footer?: ReactNode;
  width?: number;
}

/** Centered modal with the canvas header/footer anatomy. */
export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, title, subtitle, actions, footer, width = 720, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <Overlay />
    <DialogPrimitive.Content
      ref={ref}
      style={{ width, maxWidth: "calc(100vw - 32px)" }}
      className={cn(
        "fixed top-1/2 left-1/2 z-50 flex max-h-[85vh] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-lg bg-surface shadow-modal " +
          "focus-visible:outline-none",
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-2.5 border-b border-border-subtle px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-col">
          <DialogPrimitive.Title className="font-semibold text-fg">{title}</DialogPrimitive.Title>
          {subtitle ? (
            <DialogPrimitive.Description className="truncate font-mono text-sm text-fg-muted">
              {subtitle}
            </DialogPrimitive.Description>
          ) : (
            <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
          )}
        </div>
        {actions}
        <DialogPrimitive.Close
          aria-label="Close"
          className="flex size-7 items-center justify-center rounded-sm text-fg-muted hover:bg-surface-inset hover:text-fg"
        >
          <X className="size-4" aria-hidden />
        </DialogPrimitive.Close>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">{children}</div>
      {footer ? (
        <div className="flex justify-end gap-2 border-t border-border-subtle bg-surface-subtle px-4 py-3">
          {footer}
        </div>
      ) : null}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
DialogContent.displayName = "DialogContent";
